<template>
  <div>
    <ReportMobileFrame
      v-if="$vuetify.breakpoint.smAndDown"
      :loading="isLoading"
      :empty="!isLoading && data.length === 0"
      empty-text="No write-offs found for the selected range."
    >
      <template #filters>
        <div class="report-dates-grid">
          <MobileDateField label="Start Date" :value="dates[0]" @input="onMobileDateChange(0, $event)" />
          <MobileDateField label="End Date" align="right" :value="dates[1] || dates[0]" @input="onMobileDateChange(1, $event)" />
        </div>
      </template>

      <div v-for="group in groupedByDate" :key="group.dateKey">
        <ReportGroupHeader :label="group.dateLabel" />
        <ReportListRow v-for="(item, idx) in group.items" :key="group.dateKey + '-' + idx">
          <template #leading>
            <div class="report-payment-badge report-payment-badge--split">
              <v-icon dark size="18">mdi-archive-remove-outline</v-icon>
            </div>
          </template>
          <template #title>{{ item.product ? item.product.name : '—' }}</template>
          <template #subtitle>
            {{ item.user || '—' }}{{ item.reason ? ' · ' + item.reason : '' }}
          </template>
          <template #trailing>
            <span class="report-money">£{{ Number(item.amount || 0).toFixed(2) }}</span>
            <span class="report-time">{{ moment(item.writeOffAt).format('HH:mm') }}</span>
          </template>
        </ReportListRow>
      </div>

      <template #action>
        <v-btn block color="success" :loading="isLoading" x-large @click="exportExcel">
          <v-icon left>mdi-file-export-outline</v-icon>
          EXPORT REPORT
        </v-btn>
      </template>
    </ReportMobileFrame>

    <template v-else>
    <v-row>
      <v-toolbar flat class="toolbar-container mb-2" height='100'>
        <v-col>
          <div class="toolbar-datepicker">
            <DateRangePicker :initialDates="dates" @blur="updateDates"  />
          </div>
        </v-col>
      </v-toolbar>
    </v-row>
    <v-row>
      <v-col>
        <v-btn block class="search-btn" type="button" color="success" v-on:click="load">
          SEARCH
        </v-btn>
      </v-col>
      <v-col>
        <v-btn block type="button" color="primary" v-on:click="exportExcel()">
          EXPORT
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-data-table 
        :headers="headers" :items="data" 
        :loading="isLoading"
        item-key="refundedAt" class="elevation-1 mt-5 flex-grow-1">

        <template v-slot:[`item.amount`]="{ item }">
          £{{ item.amount.toFixed(2) }}
        </template>

        <template v-slot:[`item.writeOffAt`]="{ item }">
          {{ moment(item.writeOffAt).format("DD/MM/yyyy HH:mm:ss") }}
        </template>
      </v-data-table>
    </v-row>
    </template>
  </div>
</template>

<script>
import DateRangePicker from '@/components/DateRangePicker.vue';
import ReportMobileFrame from '@/components/reports/ReportMobileFrame.vue';
import ReportGroupHeader from '@/components/reports/ReportGroupHeader.vue';
import ReportListRow from '@/components/reports/ReportListRow.vue';
import MobileDateField from '@/components/reports/MobileDateField.vue';
import { reportService, productService } from '@/services';
import { b64toBlob } from '@/helpers'
import { saveAs } from 'file-saver';
import moment from 'moment';
import { userService } from '@/services';
import router from "../../router";

export default {

  name: 'WriteOff',
  components: {
    DateRangePicker,
    ReportMobileFrame,
    ReportGroupHeader,
    ReportListRow,
    MobileDateField
  },
  data: vm => ({
    dates: [moment().add(-7, 'd').toISOString().substr(0, 10), moment().toISOString().substr(0, 10)],

    isLoading: false,
    data: [],
    headers: [
      { text: 'Write Off At', align: 'start', sortable: true, filterable: true, value: 'writeOffAt' },
      { text: 'Amount', align: 'start', sortable: true, filterable: true, value: 'amount' },
      { text: 'Reason', align: 'stretch', sortable: true, filterable: true, value: 'reason' },
      { text: 'User', align: 'start', sortable: true, filterable: true, value: 'user' },
      { text: 'Product', align: 'start', sortable: true, filterable: true, value: 'product.name' },
    ],
  }),
  created () {
    if (userService.isStaff()) {
      router.push('/products');
    }
    this.load();
  },
  methods: {
    updateDates(newDates) {
      this.dates = newDates;
    },
    async load() {
      var startDate = this.dates[0];
      var endDate = this.dates[0];
      if (this.dates.length > 1)
        endDate = this.dates[1];

      this.isLoading = true;
      this.data = await reportService.getWriteOffReport(startDate, endDate);
      this.isLoading = false;
    },
    exportExcel: async function() {
      this.isLoading = true;
      var startDate = this.dates[0];
      var endDate = this.dates[0];
      if (this.dates.length > 1)
        endDate = this.dates[1];

      var response = await reportService.exportWriteOffReport(startDate, endDate);
      if (response == null) {
        alert("Error exporting");
        return;
      }

      const blob = b64toBlob(response, 'application/vnd.ms-excel');
      console.log(blob);
      saveAs(blob, 'WriteOff.xls');

      this.isLoading = false;
    },
    onMobileDateChange(index, value) {
      if (!value) return;
      const next = this.dates.slice();
      while (next.length <= index) next.push(value);
      next[index] = value;
      if (index === 0) next[1] = value;
      this.dates = next;
      this.load();
    }
  },
  computed: {
    groupedByDate() {
      const sorted = this.data.slice().sort((a, b) => new Date(b.writeOffAt) - new Date(a.writeOffAt));
      const groups = new Map();
      for (const item of sorted) {
        const m = this.moment(item.writeOffAt);
        const key = m.format('YYYY-MM-DD');
        if (!groups.has(key)) groups.set(key, { dateKey: key, dateLabel: m.format('DD MMM YYYY'), items: [] });
        groups.get(key).items.push(item);
      }
      return Array.from(groups.values());
    }
  }
}
</script>

<style lang="scss" scoped>
 .toolbar-container {
    background: #FFF !important;
    padding: 10px;
    height: auto;
    border-radius: 8px;

    border: 1px solid lighten(#7B5CD8, 30);
  }

  .toolbar-content {
    display: flex;
    flex-direction: row;
    width: 100%;

    .toolbar-datepicker {
      width: 250px;
    }
  }
</style>
